<template>
  <div style="display: contents;">
    <ValidationObserver ref="bookingForm" slim>
      <b-form name="BookingPaxFrm" novalidate @submit.prevent="book()">
        <b-row>
          <b-container class="content">
            <div class="printArea">
              <SearchParamsSummary :params="params" />
              <HotelInfo :result="result" />
            </div>
            <b-row>
              <b-col sm="12" class="printArea">
                <b-table-simple stacked="sm" striped caption-top  class="table table-sm text-center border">
                  <caption class="pl-2 pl-sm-0 pb-0"><h6 class="package-title" v-translate>Passengers and room details</h6></caption>
                  <b-thead class="t-head thead-light text-center">
                    <b-tr>
                      <b-td><translate>Room</translate></b-td>
                      <b-td></b-td>
                      <b-td><translate>Last name</translate>*</b-td>
                      <b-td><translate>First name</translate>*</b-td>
                      <b-td><translate>Age</translate></b-td>
                      <b-td><translate>Date of birth</translate>*</b-td>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-for="(room,rIndex) in pax" :key="rIndex">
                    <template v-for="(p, pIndex) in room">
                      <PaxForm
                        class="noPrint"
                        v-model="room[pIndex]"
                        :travelDate="travelDate"
                        :room="rIndex + 1"
                        :id="'room' + rIndex + 'pax' + pIndex"
                        :key="'pax' + pIndex"
                      />
                      <div :key="pIndex" style="display: none">
                        <tr>
                          <td>{{ rIndex + 1 }}</td>
                          <td>{{ room[pIndex].title }}</td>
                          <td>{{ room[pIndex].lname }}</td>
                          <td>{{ room[pIndex].fname }}</td>
                          <td>{{ room[pIndex].age }}</td>
                          <td>{{ room[pIndex].bdate }}</td>
                        </tr>
                      </div>
                    </template>
                  </b-tbody>
                </b-table-simple>
              </b-col>
            </b-row>
            <InvoicingForm v-model="client" v-if="userData.is_mandated === 't'"/>
            <b-row>
              <b-col md="12">
                <hr>
                <small class="text-danger d-block"><em>*<translate>Fields marked with asterisks are mandatory</translate>.</em></small>
                <small class="text-danger"><em>*<translate>All passengers under 18 are considered to be children</translate></em></small>
              </b-col>
            </b-row>
            <b-row class="my-3">
              <b-col>
                <div class="printArea">
                <h6><translate>Agent notes</translate>*</h6>
                <textarea v-model="notes" class="form-control noPrint">...</textarea>
                <div class="printArea d-none">
                    <p>{{ notes }}</p>
                  </div>
                </div>
                <span>
                  * <translate>In this field you can enter any notes or additional information related to the reservation.</translate>
                  * <translate>Special requests will be forwarded to service providers, but they cannot be guaranteed!</translate>
                </span>
              </b-col>
            </b-row>
            <b-row class="my-3 printArea">
              <b-col>
                <b-card no-header>
                  <b-row>
                    <b-col>
                      <b><translate>Selected room category</translate>:</b> {{ category.name }}
                    </b-col>
                    <b-col>
                      <b><translate>Selected meal plan</translate>:</b> {{ meal.label }}
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="7">
                <b-card>
                  <ValidationProvider
                    name="terms"
                    rules="is:accepted"
                    vid="terms"
                    v-slot="vContext"
                    slim
                  >
                    <b-form-group>
                      <b-form-checkbox
                        id="conditions"
                        name="conditions"
                        v-model="termsAccepted"
                        :state="getValidationState(vContext)"
                        aria-describedby="conditions-live-feedback"
                        value="accepted"
                        unchecked-value="unaccepted"
                        >
                          <translate>I have read and agree to the </translate> <span class="text-info" @click="openTermsAndConditions" v-translate> Terms and Conditions</span>
                        </b-form-checkbox>
                      <b-form-invalid-feedback id="conditions-live-feedback" :state="getValidationState(vContext)"><translate>you have not accepted the terms and conditions</translate></b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>
                </b-card>
              </b-col>
              <b-col md="5" class="printArea">
                <b-card
                  style="font-size:16px;"
                  class="text-center bg-ligth-brown"
                >
                  <div class="d-flex justify-content-between">
                    <span class="flex-fill"><b class="text-primary"><translate>Price</translate>:</b><br/>
                      <span class="text-danger font-weight-bold">{{ price }} {{ params.currency }} </span><br/><small>(<translate>tax included</translate>)</small></span>
                    <span class="flex-fill">
                      <small v-for="(dd, ddIndex) in paymentSchedule" :key="'dd' + ddIndex">
                        {{ formatDate(dd.date) }} - <b>{{ ceil(dd.amount) || $gettext('balance') }}</b><br/>
                      </small>
                    </span>
                  </div>
                </b-card>
              </b-col>
            </b-row>
            <b-row class="mt-4">
              <b-col>
                <b-card
                  class="bg-ligth-brown"
                  no-body
                >
                  <b-card-body class="p-1 p-md-3">
                    <div class="d-md-flex justify-content-md-between">
                      <b-button variant="primary" size="sm" @click="back" class="mb-2 mb-md-0"><b-icon icon="chevron-left" /><translate>Back to results</translate></b-button>
                      <b-button-group size="sm">
                          <button class="btn btn-outline-primary btn-sm " @click.prevent="onPrint" v-b-tooltip.hover :title="$gettext('print')">
                            <b-icon icon="window" /> <translate>Print details</translate>
                          </button>
                          <button class="btn btn-outline-danger btn-sm" @click.prevent="onDownload" v-b-tooltip.hover :title="$gettext('PDF')">
                            <b-icon icon="download" /> <translate>PDF details</translate>
                          </button>
                          <b-button
                            v-if="category.allowOption"
                            variant="info"
                            @click.prevent="book(true)"
                            v-b-popover.hover.top="$gettext('Bookings made as OPTION are not guaranteed: transport is generally available, but the availability of the hotel can change!')"
                            :title="$gettext('Information about OPTION bookings')" v-translate>Option</b-button>
                          <b-button variant="primary" @click.prevent="book(false)" type="submit" v-translate>Book</b-button>
                        </b-button-group>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-container>
        </b-row>
      </b-form>
    </ValidationObserver>
    <b-modal v-model="showOverlay" title="Status" size="xl" hide-header hide-footer no-close-on-esc no-close-on-backdrop>
      <b-card>
        <b-aspect class="align-items-center text-center" aspect="3">
          <b-spinner label="Loading..." variant="primary"></b-spinner>
          <b-card-text>{{ overlayMessage }}</b-card-text>
        </b-aspect>
      </b-card>
    </b-modal>
    <b-modal v-model="showErrorOverlay" title="Error" size="xl" hide-header hide-footer no-close-on-esc no-close-on-backdrop>
      <b-card class="justify-content-center">
        <b-aspect aspect="3" class="align-items-center">
          <b-card-title class=" font-weight-bold text-center">
            <b-icon icon="exclamation-triangle" class="text-danger mr-3"/>
           <translate> There was a problem</translate>
          </b-card-title>
          <hr>
          <b-card-text class="lead text-muted" v-translate>Unfortunately the reservation could not be registered because a problem occurred.</b-card-text>
          <b-card-text class="lead text-muted" v-translate>Generally, this happens when the price or availability of the product has changed since you searched.</b-card-text>
          <b-card-text class="lead text-muted"><translate>The error message is</translate>: <b class="text-info">{{ errorMessage }}</b></b-card-text>
        </b-aspect>
        <hr>
        <b-button variant="warning float-right" size="sm" @click="back" v-translate>Back to results</b-button>
      </b-card>
    </b-modal>
    <b-modal v-model="termsAndConditions" :title="$gettext('Terms and Conditions')" size="xl" hide-header hide-footer no-close-on-esc no-close-on-backdrop>
      <b-card>
        <TermsAndConditions/>
        <hr>
        <b-button-group size="sm" class="float-right">
          <b-button variant="danger"  @click.prevent="onDownloadTermsAndConditions" v-b-tooltip.hover :title="$gettext('PDF')"><b-icon icon="download" /> <translate>Download pdf</translate></b-button>
          <b-button variant="warning"  @click="closeTermsAndConditions" v-translate>Close</b-button>
        </b-button-group>
      </b-card>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'

import model from '../services/model'
import toast from 'b2b-common/mixins/toast'
import auth from 'b2b-common/mixins/auth'
import validation from 'b2b-common/mixins/validation'
import SearchParamsSummary from './search_params_summary'
import HotelInfo from '@/app/private/shared/components/hotel_info'
import PaxForm from '@/app/private/shared/components/pax_form'
import InvoicingForm from '@/app/private/shared/components/invoicing_info_form'
import TermsAndConditions from '../../shared/components/terms_and_conditions'
import { $gettext } from '@/config/gettext'

import { getCustomPdf, downloadPdf, openPrintPage } from 'b2b-common/services/api/document'
import { search, book, paymentSchedule } from 'b2b-common/services/api/hotel'

const calculatePrice = (category, meal) => {
  const p = meal.price + meal.tax
  return p
}

const invalidFormMessage = $gettext('Invalid form')

export default {
  mixins: [toast, auth, validation],
  props: ['params', 'result', 'pack', 'category', 'meal'],
  components: { HotelInfo, SearchParamsSummary, InvoicingForm, PaxForm, TermsAndConditions },
  computed: {
    price () {
      return calculatePrice(this.category, this.meal, this.selectedExtras)
    }
  },
  data () {
    const travelDate = moment(this.params.checkIn, 'DD-MM-YYYY')

    const rooms = []
    let noAdults = 0
    let noChildren = 0
    this.params.rooms.forEach(room => {
      const pax = []
      noAdults += room.adults
      noChildren += room.ages.length
      for (let i = 0; i < room.adults; i++) {
        pax.push({
          title: 'Mr',
          fname: '',
          lname: '',
          age: 'adult',
          bdate: null
        })
      }
      room.ages.forEach(age => {
        pax.push({
          title: (age < 2) ? 'Inf' : 'Chd',
          fname: '',
          lname: '',
          age: age,
          bdate: null
        })
      })
      rooms.push(pax)
    })

    return {
      travelDate,
      noAdults,
      noChildren,
      notes: '',
      pax: rooms,
      termsAccepted: 'unaccepted',
      client: {
        clientCode: '',
        isCompany: false,
        companyName: '',
        companyNo: '',
        vatNo: '',
        bankAccount: '',
        bankName: '',
        title: 'Mr',
        firstName: '',
        lastName: '',
        idInfo: '',
        cnp: '',
        address1: '',
        address2: '',
        county: '',
        city: '',
        tel: '',
        email: ''
      },
      showErrorOverlay: false,
      showOverlay: false,
      overlayMessage: '',
      errorMessage: this.$gettext('unknown error'),
      termsAndConditions: false,
      paymentSchedule: []
    }
  },
  async created () {
    this.paymentSchedule = await paymentSchedule({
      source: this.result.source,
      category: this.category.id,
      meal: this.meal.id
    })
  },
  methods: {
    onPrint () {
      this.$refs.bookingForm.validate()
        .then(success => {
          if (!success) {
            this.addToast({
              title: invalidFormMessage,
              message: this.$gettext('Please fill in all required fields!'),
              variant: 'danger'
            })
            return
          }
          const arr = document.getElementsByClassName('printArea')
          let html = ''
          arr.forEach(el => {
            html += el.innerHTML
          })
          openPrintPage({ html })
        })
    },
    onDownload () {
      this.$refs.bookingForm.validate()
        .then(success => {
          if (!success) {
            this.addToast({
              title: invalidFormMessage,
              message: this.$gettext('Please fill in all required fields!'),
              variant: 'danger'
            })
            return
          }
          const arr = document.getElementsByClassName('printArea')
          let html = ''
          arr.forEach(el => {
            html += el.innerHTML
          })
          getCustomPdf({ html })
            .then(pdfData => {
              downloadPdf({ pdfData, fileName: 'oferta.pdf', dataType: 'raw' })
            })
        })
    },
    onDownloadTermsAndConditions () {
      const arr = document.getElementsByClassName('printTerms')
      let html = ''
      arr.forEach(el => {
        html += el.innerHTML
      })
      getCustomPdf({ html })
        .then(pdfData => {
          downloadPdf({ pdfData, fileName: 'contract.pdf', dataType: 'raw' })
        })
    },
    back () {
      this.$emit('back')
    },
    openTermsAndConditions () {
      this.termsAndConditions = true
    },
    closeTermsAndConditions () {
      this.termsAndConditions = false
    },
    async book (isOption = false) {
      try {
        const success = await this.$refs.bookingForm.validate()

        if (!success) {
          this.addToast({ title: invalidFormMessage, message: this.$gettext('Please fill in all required fields!'), variant: 'danger' })
          throw new Error(invalidFormMessage)
        }
        this.showOverlay = true
        this.overlayMessage = this.$gettext('Please wait, we check again the price and availability of the selected product ...')

        let bookParams = {}

        const pax = [...this.pax.map(room => room.map(p => ({
          title: p.title,
          fname: p.fname.toUpperCase(),
          lname: p.lname.toUpperCase(),
          age: p.age,
          bdate: p.bdate
        })))]

        const newParams = model.params
        newParams.hotels = [this.result.id]
        model.params = newParams
        const data = await search(newParams)

        // re-populate model
        model.results = data
        model.sort({ sortMethod: model.sortMethod })

        let newResult = null
        let newCategory = null
        let newMeal = null

        model.results.forEach(result => {
          if (result.id !== this.result.id) return
          result.categories.forEach(category => {
            if (this.category.name !== category.name ||
                  category.hotelIsAvailable !== this.category.hotelIsAvailable ||
                  category.hotelIsBlackedOut !== this.category.hotelIsBlackedOut ||
                  category.isBookable !== this.category.isBookable) return
            category.meals.forEach(meal => {
              const p1 = meal.price + meal.tax
              const p2 = this.meal.price + this.meal.tax
              if (meal.code !== this.meal.code ||
                    meal.label !== this.meal.label ||
                    p1 !== p2) return
              newResult = result
              newCategory = category
              newMeal = meal
            })
          })
        })

        if (!newResult) throw new Error(this.$gettext('Could not find hotel'))
        if (!newCategory) throw new Error(this.$gettext('Difference in availability found'))
        if (!newMeal) throw new Error(this.$gettext('Price difference found'))

        // now ok to create booking
        bookParams = {
          source: newResult.source,
          category: newCategory.id,
          meal: newMeal.id,
          notes: this.notes,
          pax: pax
        }

        if (isOption) bookParams.status = 'option'
        const res = await book(bookParams)
        model.results = []
        this.$emit('book', res[0])
      } catch (err) {
        if (err.message !== invalidFormMessage) {
          this.errorMessage = err.message
          this.showErrorOverlay = true
        }
      } finally { this.showOverlay = false }
    },
    onExtraToggle (extraIndex) {
      const index = this.selectedExtras.indexOf(extraIndex)
      if (index >= 0) this.selectedExtras.splice(index, 1)
      else this.selectedExtras.push(extraIndex)
    },
    formatDate (d) {
      return moment(d, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },
    ceil (no) {
      if (no === null || no === undefined) return null
      return Math.ceil(no) + this.params.currency
    }
  }
}
</script>
